<template>
  <div class="indicators">
    <template v-if="summary">
      <div
        v-for="(item, index) in indicators"
        :key="'indicator' + index"
        :data-key="item.key"
        class="item"
      >
        <div class="line-clamp-1 title">{{ item.title }}</div>
        <div
          v-if="item.html"
          class="data"
          v-html="item.html"
        ></div>
        <div
          v-else
          class="data"
        >
          <div class="value">
            {{ item.valueText }}
            <span class="unit">{{ item.unit }}</span>
          </div>
        </div>
      </div>
      <!--  训练重点-->
      <div
        v-if="showTrainingFocusInfo"
        class="item"
      >
        <div class="line-clamp-1 title">{{ $t(`H1117`) }}</div>
        <div class="row-flex align-item-center justify-center data">
          <span class="value">
            {{ isNullValue(trainingFocusInfo) ? '- -' : trainingFocusInfo.text }}
          </span>
        </div>
      </div>
      <!-- 运动表现-->
      <div
        v-if="performanceInfo.displayPerformance"
        class="item"
      >
        <div class="line-clamp-1 title">
          {{ $t(`SQ1302`) }}
          <span
            @click="handleTipClick(0)"
            class="iconfont iconchumoicon5 cursor-pointer text-gray-300 text-12"
          ></span>
        </div>
        <div class="row-flex align-item-center justify-center data">
          <div class="value">
            <template v-if="!performanceInfo.noValue">
              <span>{{ performanceInfo.staminaLevel7d }}</span>
              <span
                class="unit color-box"
                :style="{ 'background-color': performanceInfo.color }"
                >{{ performanceInfo.text }}</span
              >
            </template>
            <template v-else>
              <a-tooltip :content="performanceInfo.tip">
                <span class="text-primary-5 text-14">{{ performanceInfo.staminaLevel7d }}</span>
              </a-tooltip>
            </template>
          </div>
        </div>
      </div>
      <!--  达标率-->
      <div
        v-if="summary?.programId"
        class="item"
      >
        <div class="line-clamp-1 title">
          {{ $t(`TD1065`) }}
          <span
            @click="handleTipClick(11)"
            class="iconfont iconchumoicon5 cursor-pointer text-gray-300 text-12"
          ></span>
        </div>
        <div class="data">
          <span class="value">
            {{ sportSummary.StandardRate.valueText }}
            <span class="unit">{{ sportSummary.StandardRate.unit }}</span>
          </span>
        </div>
      </div>
      <!--  有氧效果-->
      <div
        v-if="aerobicEffect"
        class="item"
      >
        <div class="line-clamp-1 title">
          {{ $t(`H1102`) }}
          <span
            @click="handleTipClick(10)"
            class="iconfont iconchumoicon5 cursor-pointer text-gray-300 text-12"
          ></span>
        </div>
        <div class="row-flex align-item-center justify-center data">
          <div class="value">
            <span>{{ isNullValue(aerobicEffect) ? '- -' : aerobicEffect }}</span>
            <span
              v-show="!isNullValue(aerobicEffect)"
              class="unit color-box"
              :style="{ 'background-color': aerobicEffectInfo.color }"
              >{{ aerobicEffectInfo.text }}</span
            >
          </div>
        </div>
      </div>
      <!--  无氧效果-->
      <div
        v-if="anaerobicEffect"
        class="item"
      >
        <div class="line-clamp-1 title">
          {{ $t(`H1103`) }}
          <span
            @click="handleTipClick(10)"
            class="iconfont iconchumoicon5 cursor-pointer text-gray-300 text-12"
          ></span>
        </div>
        <div class="row-flex align-item-center justify-center data">
          <span class="value">
            {{ isNullValue(anaerobicEffect) ? '- -' : anaerobicEffect }}
            <span
              v-show="!isNullValue(anaerobicEffect)"
              class="unit color-box"
              :style="{ 'background-color': anaerobicEffectInfo.color }"
              >{{ anaerobicEffectInfo.text }}</span
            >
          </span>
        </div>
      </div>
    </template>
  </div>
</template>

<script setup>
import { computed, getCurrentInstance } from 'vue';
import { Message } from '@arco-design/web-vue';
import { activityDetailStore } from '@client/store/activityDetailStore';
import unit from '@client/helper/unit';
import {
  getAnaerobicEffectRange,
  getTrainingFocusRange,
  getAerobicEffectRange,
  performanceRange,
  getPerformanceNoValueReason
} from '@client/pages/activity-detail/config/ranges';
import { getSportSummary, getSportSummaryItems } from '@client/pages/activity-detail/config/sport.summary';
import Definition from '@client/config/definition';

import { useTipsModal } from '@client/pages/layout/components/tipsModal/useTipsModal';
import { topSummary, useActivityDetail } from '../../hooks/useActivityDetail';

const vm = getCurrentInstance();
Message._context = vm?.appContext;
const t = vm.proxy.$root.$t;
const activityDetailStoreRef = activityDetailStore();
const { unitType } = useActivityDetail();

const summary = computed(() => activityDetailStoreRef.activityDetail?.summary);

// 训练重点
const showTrainingFocusInfo = computed(() => {
  return [Definition.getSportTypeBySportName('Run'), Definition.getSportTypeBySportName('Track_Run')].includes(
    `${activityDetailStoreRef.sportType}`
  );
});
const trainingFocusInfo = computed(() => {
  const index = summary.value?.trainType || 0;
  return getTrainingFocusRange()[index];
});
const isCustomSport = computed(() => {
  return Definition.isCustomSport(topSummary.value.sportType);
});

const indicators = computed(() => {
  const { activityDetail } = activityDetailStoreRef;
  const { summary } = activityDetail;
  // topSummary.sportType 是父运动的 sportType
  // summary.sportType 是子运动的 sportType
  const sportName = Definition.getSportNameByType(summary.sportType);
  const summaryKeys = getSportSummaryItems(isCustomSport.value, topSummary.value)[sportName] || [];
  const result = [];

  summaryKeys.forEach((data) => {
    const key = data.key;
    const item = sportSummary.value[key];

    // 判断是否需要显示该数据项
    if (item.useIt && !item.useIt()) {
      return;
    }

    result.push({
      ...item,
      key,
      title: item.title,
      html: item.html || getDefaultHtml(item.valueText, item.unit) || ''
    });
  });
  return result;
});
const performanceInfo = computed(() => {
  let result = {
    text: '',
    color: 'transparent',
    displayPerformance: false,
    performance: 0,
    staminaLevel7d: 0,
    noValue: false,
    tip: ''
  };
  const detail = activityDetailStoreRef.activityDetail;
  const summary = detail?.summary;
  if (!detail || !summary) return '';
  // displayPerformance 是否展示
  // performance 表现分级, 优, 良好,一般等
  // noPerformanceReason 无表现原因
  // noPerformanceReasonTip 无表现原因提示
  // staminaLevel7d, noPerformanceReason, noPerformanceReasonTip
  const { displayPerformance, performance, sportType, sportName, staminaLevel7d, noPerformanceReason, noPerformanceReasonTip } = summary;
  // let staminaLevel7d = 0;
  // let noPerformanceReason = 4;
  // let noPerformanceReasonTip = '80';
  result.displayPerformance = displayPerformance;
  result.performance = performance;
  result.staminaLevel7d = staminaLevel7d + '%';
  if (unit.isNoValue(staminaLevel7d) || [0, '0'].includes(staminaLevel7d)) {
    // 展示, 但是不出值
    result.text = '';
    result.color = 'transparent';
    result.noValue = true;
    result.staminaLevel7d = t('R9032');
    // 获取不出值原因
    let sportCategoryName;

    if (['Run', 'Indoor_Run', 'Trail_Run', 'Track_Run', 'Hike', 'Mtn_Climb'].includes(sportName)) {
      sportCategoryName = 'run';
    } else if (
      ['Bike', 'Indoor_Bike', 'Road_Ebike', 'Gravel_Road_Bike', 'Mountain_Riding', 'Mountain_Ebike', 'Helmet_Bike'].includes(sportName)
    ) {
      sportCategoryName = 'bike';
    }
    result.tip = getPerformanceNoValueReason(sportCategoryName, noPerformanceReason, noPerformanceReasonTip);
  } else {
    const targetLabelItem = performanceRange.find((item) => item.value === performance);
    if (targetLabelItem) {
      const { color, text } = targetLabelItem;
      result.color = color;
      result.text = text;
    }
  }
  return result;
});
const sportSummary = computed(() => {
  if (!summary.value) return {};
  const sportName = Definition.getSportNameByType(activityDetailStoreRef.sportType);
  return getSportSummary({
    sportName,
    unitType: unitType.value,
    // 图表相关
    // 接口返回的y轴刻度定义
    graphObj: activityDetailStoreRef.graphObj,
    summary: summary.value,
    isCustomSport
  });
});

// 有氧
const aerobicEffect = computed(() => {
  return summary.value?.aerobicEffect?.toFixed(1) || '';
});
const aerobicEffectInfo = computed(() => {
  let result = {
    text: '',
    color: 'transparent'
  };
  const aerobicEffectRange = getAerobicEffectRange();
  for (let n = 0, length = aerobicEffectRange.length; n < length; n++) {
    const item = aerobicEffectRange[n];
    const range = item.range;
    if (n === length - 1) {
      if (range[0] <= aerobicEffect.value && aerobicEffect.value <= range[1]) {
        result = item;
        break;
      }
    } else if (range[0] <= aerobicEffect.value && aerobicEffect.value < range[1]) {
      result = item;
      break;
    }
  }
  return result;
});

// 无氧效果
const anaerobicEffect = computed(() => {
  let result = '';
  try {
    result = summary.value.anaerobicEffect.toFixed(1);
  } catch (e) {
    result = '';
  }
  return result;
});
const anaerobicEffectInfo = computed(() => {
  let result = {
    text: '',
    color: 'transparent'
  };
  const anaerobicEffectRange = getAnaerobicEffectRange();
  for (let n = 0, length = anaerobicEffectRange.length; n < length; n++) {
    const item = anaerobicEffectRange[n];
    const range = item.range;
    if (n === length - 1) {
      if (range[0] <= anaerobicEffect.value && anaerobicEffect.value <= range[1]) {
        result = item;
        break;
      }
    } else if (range[0] <= anaerobicEffect.value && anaerobicEffect.value < range[1]) {
      result = item;
      break;
    }
  }
  return result;
});

function isNullValue(value) {
  return unit.isNoValue(value);
}
const { setTipsModalActiveIndex } = useTipsModal();
function handleTipClick(type = 1) {
  setTipsModalActiveIndex(type);
}
function getDefaultHtml(value, unit) {
  return `<div class="value">
              <span>${value}</span>
              <span class="unit">${unit || ''}</span>
          </div>`;
}
</script>

<style lang="scss">
@import '@client/styles/_mixins.scss';
.arco-collapse-item-content {
  padding: 0;
}
</style>

<style lang="scss" scoped>
@import '@client/styles/_mixins.scss';

.indicators {
  padding: 0 px2rem144(16);
  height: 100%;
  overflow: auto;
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  grid-auto-rows: px2rem144(60);
  grid-gap: 1px 1px;

  .item {
    width: 100%;
    height: 100%;
    display: flex;
    flex-direction: column;
    justify-content: center;
    text-align: center;
    border-right: 1px solid rgba(255, 255, 255, 0.05);
    border-bottom: 1px solid rgba(255, 255, 255, 0.05);
    box-sizing: border-box;

    &:nth-of-type(2n) {
      border-right: 0;
    }

    .title {
      margin-bottom: px2rem144(8);
      @apply text-12;
      @apply font-normal;
      color: rgba(255, 255, 255, 0.6);

      .icon-analysis {
        width: px2rem144(12);
        cursor: pointer;
        display: inline;
      }
    }

    .data {
      @apply flex;
      @apply flex-row;
      @apply justify-center;
      @apply items-center;

      :deep(.value) {
        margin-left: px2rem144(-8);
        position: relative;
        @apply text-16;
        @apply font-medium;
        @apply text-white;
        overflow: visible;
        .unit {
          position: absolute;
          top: 50%;
          left: 100%;
          @apply w-full;
          @apply text-left;
          transform: translate(0, -50%);
          margin-left: px2rem144(4);
          @apply text-12;
          @apply font-normal;
          color: rgba(255, 255, 255, 0.4);
          word-break: keep-all;

          &.color-box {
            transform: translate(-17%, -50%) scale(0.66);
            height: px2rem144(18);
            line-height: px2rem144(18);
            @apply px-4;
            border-radius: px2rem144(12);
            @apply text-12;
            white-space: nowrap;
            @apply text-white;
            background: #12b1f5;
            @apply text-center;
            width: auto;
          }

          &.time {
            top: 0;
            left: 100%;
            transform: translate(-10%, 0) scale(0.8);
            @apply text-white;
          }
        }
        .percentUnit {
          @apply text-12;
          @apply font-normal;
          color: rgba(255, 255, 255, 0.4);
        }
      }
    }
  }
}
</style>
